import * as THREE from 'three';
import type { WorldTheme } from '../core/types';
import { WORLD_THEMES } from './palette';

/** Soft decorative props behind the board for world identity. */
export function createEnvironment(theme: WorldTheme): THREE.Group {
  const root = new THREE.Group();
  const t = WORLD_THEMES[theme];

  // Floating runic rings
  const ringMat = new THREE.MeshStandardMaterial({
    color: t.frame,
    emissive: t.rim,
    emissiveIntensity: 0.25,
    metalness: 0.5,
    roughness: 0.35,
    transparent: true,
    opacity: 0.55,
    side: THREE.DoubleSide,
  });

  for (let i = 0; i < 3; i++) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(3.5 + i * 0.7, 0.035, 8, 48), ringMat);
    ring.position.set(0, 0.2, -1.5 - i * 0.4);
    ring.rotation.x = Math.PI / 2.4 + i * 0.1;
    ring.rotation.z = i * 0.4;
    ring.userData.spin = 0.05 + i * 0.02;
    root.add(ring);
  }

  // Side pillars / crystals
  const pillarGeo = new THREE.CylinderGeometry(0.15, 0.25, 2.2, 6);
  const pillarMat = new THREE.MeshStandardMaterial({
    color: t.board,
    emissive: t.key,
    emissiveIntensity: 0.15,
    roughness: 0.45,
    metalness: 0.3,
  });
  for (const side of [-1, 1]) {
    const p = new THREE.Mesh(pillarGeo, pillarMat);
    p.position.set(side * 6.2, -0.5, -1);
    root.add(p);
    const cap = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.35, 0),
      new THREE.MeshStandardMaterial({
        color: t.rim,
        emissive: t.rim,
        emissiveIntensity: 0.5,
        roughness: 0.25,
        metalness: 0.6,
      })
    );
    cap.position.set(side * 6.2, 0.9, -1);
    root.add(cap);
  }

  // Ground glow disc
  const disc = new THREE.Mesh(
    new THREE.CircleGeometry(7, 48),
    new THREE.MeshBasicMaterial({
      color: t.key,
      transparent: true,
      opacity: 0.08,
      depthWrite: false,
    })
  );
  disc.rotation.x = -Math.PI / 2;
  disc.position.set(0, -4.5, 0);
  root.add(disc);

  root.userData.theme = theme;
  return root;
}

export function updateEnvironment(root: THREE.Group, time: number, reducedMotion: boolean): void {
  if (reducedMotion) return;
  for (const child of root.children) {
    if (child.userData.spin) {
      child.rotation.z += child.userData.spin * 0.016;
    }
  }
  root.rotation.y = Math.sin(time * 0.08) * 0.03;
}
